import { Dialog, DialogContent, DialogTitle, Grid } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { Form, Formik } from "formik";
import React, { useCallback, useMemo, useState } from "react";
import { useQueryClient } from "react-query";

import { SelectOption } from "components/form/SelectOption";
import { FormDialogActions } from "components/form/standard/FormDialogActions";
import {
  deriveYupSchemaFromMetadataV3,
  getInitialValuesV3,
  getOnSubmitV3,
} from "components/form/standard/utils/formMetadata";
import { TextFieldString } from "components/form/TextFieldString";
import { AccountTypeField } from "components/form_v2/accounting/ledger_account/AccountTypeField";
import { ConfirmDeleteDialog } from "components/form_v2/editable/components/ConfirmDeleteDialog";
import { LedgerAccountMetadata } from "constants/objectMetadata/ledgerAccountMetadata";
import { usePopForm, usePushForm } from "contexts/FormDialogsContext";
import { useUsersDisplay } from "contexts/UserDisplayContext";
import { YupRequiredFieldsProvider } from "contexts/YupRequiredFieldsContext";
import useAuth from "hooks/useAuth";
import { CloseButton } from "pages/deal/components/CloseButton";
import { useFormFieldDictQuery } from "queries/useFormField";
import { useFormLayoutQuery } from "queries/useFormLayout";
import {
  getLedgerAccountQueryKey,
  getLedgerAccountsQueryKey,
  useLedgerAccountQuery,
} from "queries/useLedgerAccount";
import {
  LedgerAccountCreate,
  LedgerAccountRead,
} from "types/api/deal/accounting/ledger_account";
import { RecordActionResponse } from "types/api/deal/api";
import {
  CustomFormEntityEnum,
  FormFieldRead,
} from "types/api/deal/dynamic_form/form_field";
import { FormLayoutRead } from "types/api/deal/dynamic_form/form_layout";
import { ACCOUNT_NORMAL_SIDE_OPTIONS } from "types/form/ledgerAccountForm";
import {
  createLedgerAccountAsync,
  deleteLedgerAccountAsync,
  updateLedgerAccountAsync,
} from "utils/ledger_account";
import { deletePropertyAsync } from "utils/property";

interface LedgerAccountFormikValues {
  id?: string;
  name: string;
}

interface LedgerAccountFormProps {
  record?: LedgerAccountRead;
  changes?: Partial<LedgerAccountFormikValues>;
  submitData: (data: any) => Promise<void>;
  formLayout: FormLayoutRead;
  formFields: Record<string, FormFieldRead>;
}

interface LedgerAccountFormProps {
  record?: LedgerAccountRead;
  changes?: Partial<LedgerAccountFormikValues>;
  submitData: (data: any) => Promise<void>;
}

function LedgerAccountForm({
  record,
  changes = {},
  submitData,
}: LedgerAccountFormProps) {
  const queryClient = useQueryClient();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const { user } = useAuth();
  const usersDisplay = useUsersDisplay();
  const popForm = usePopForm();

  const initialValues = useMemo(
    () =>
      getInitialValuesV3<LedgerAccountRead, Partial<LedgerAccountFormikValues>>(
        LedgerAccountMetadata,
        record,
        changes,
        usersDisplay,
        user
      ),
    [changes, record, user, usersDisplay]
  );

  const validationSchema = useMemo(
    () => deriveYupSchemaFromMetadataV3(LedgerAccountMetadata),
    []
  );

  const onSubmit = useMemo(
    () => getOnSubmitV3(LedgerAccountMetadata, submitData),
    [submitData]
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
        validateOnChange={false}
        validateOnBlur={true}
      >
        {({ handleSubmit }) => (
          <YupRequiredFieldsProvider validationSchema={validationSchema}>
            <Form>
              <Dialog open={true} scroll={"paper"} maxWidth={"md"}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    paddingRight: "20px",
                  }}
                >
                  <DialogTitle>
                    {record ? "Edit" : "New"} LedgerAccount{" "}
                  </DialogTitle>
                  <CloseButton onClose={popForm} />
                </div>
                <DialogContent dividers>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextFieldString
                        fieldName={"name"}
                        displayName={"Name"}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <SelectOption
                        fieldName={"normal_side"}
                        displayName={"Normal Side"}
                        options={ACCOUNT_NORMAL_SIDE_OPTIONS}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <AccountTypeField />
                    </Grid>
                  </Grid>
                </DialogContent>
                <FormDialogActions
                  canDelete={!!record?.id}
                  handleDelete={() => setDeleteModalOpen(true)}
                />
              </Dialog>
              <ConfirmDeleteDialog
                entity="Account"
                isOpen={deleteModalOpen}
                handleDeleteClose={() => setDeleteModalOpen(false)}
                handleDeleteField={() => {
                  !!record?.id &&
                    deleteLedgerAccountAsync(record.id).then(() => {
                      queryClient.invalidateQueries(
                        getLedgerAccountsQueryKey()
                      );
                      setDeleteModalOpen(false);
                      popForm();
                    });
                }}
              />
            </Form>
          </YupRequiredFieldsProvider>
        )}
      </Formik>
    </LocalizationProvider>
  );
}

interface LedgerAccountEditFormProps {
  id: string;
  changes?: Partial<LedgerAccountFormikValues>;
  handleClose: () => void;
  successCallback: (actionList: RecordActionResponse[]) => void;
}

const LedgerAccountEditForm = ({
  id,
  changes,
  handleClose,
  successCallback,
}: LedgerAccountEditFormProps) => {
  const pushForm = usePushForm();
  const { data: formLayout } = useFormLayoutQuery(
    CustomFormEntityEnum.property
  );
  const { data: formFields } = useFormFieldDictQuery(
    CustomFormEntityEnum.property
  );

  const queryClient = useQueryClient();

  const submitData = useCallback(
    async (record: LedgerAccountCreate) => {
      const responseObj = await updateLedgerAccountAsync(id, record, pushForm);
      await queryClient.invalidateQueries(getLedgerAccountQueryKey(id));
      await queryClient.invalidateQueries(["property_table"]);
      successCallback(responseObj);
      handleClose();
    },
    [handleClose, id, pushForm, queryClient, successCallback]
  );

  const { data } = useLedgerAccountQuery(id);

  return !!formLayout && !!formFields && !!data ? (
    <LedgerAccountForm
      submitData={submitData}
      formLayout={formLayout}
      formFields={formFields}
      changes={changes}
      record={data}
    />
  ) : null;
};

interface LedgerAccountCreateFormProps {
  changes?: Partial<LedgerAccountFormikValues>;
  handleClose: () => void;
  successCallback: (actionList: RecordActionResponse[]) => void;
}

const LedgerAccountCreateForm = ({
  changes,
  handleClose,
  successCallback,
}: LedgerAccountCreateFormProps) => {
  const pushForm = usePushForm();
  const { data: formLayout } = useFormLayoutQuery(
    CustomFormEntityEnum.property
  );
  const { data: formFields } = useFormFieldDictQuery(
    CustomFormEntityEnum.property
  );

  const queryClient = useQueryClient();

  const submitData = useCallback(
    async (record: LedgerAccountCreate) => {
      const responseObj = await createLedgerAccountAsync(record, pushForm);
      await queryClient.invalidateQueries(getLedgerAccountsQueryKey());
      successCallback(responseObj);
      handleClose();
    },
    [handleClose, pushForm, queryClient, successCallback]
  );

  return !!formLayout && !!formFields ? (
    <LedgerAccountForm
      submitData={submitData}
      formLayout={formLayout}
      formFields={formFields}
      changes={changes}
    />
  ) : null;
};

const LedgerAccountFormPage = ({
  id,
  handleClose,
  successCallback,
  changes,
}: {
  id?: string;
  handleClose: () => void;
  successCallback: (actionList: RecordActionResponse[]) => void;
  changes?: Partial<LedgerAccountFormikValues>;
}) => {
  return id ? (
    <LedgerAccountEditForm
      id={id}
      handleClose={handleClose}
      successCallback={successCallback}
      changes={changes}
    />
  ) : (
    <LedgerAccountCreateForm
      handleClose={handleClose}
      successCallback={successCallback}
      changes={changes}
    />
  );
};

export default LedgerAccountFormPage;
